import { z } from 'zod'

// Schema for different content types
const videoContentSchema = z.object({
  duration: z.number(),
  videoUrl: z.string(),
  resources: z.array(z.string()).optional(),
  transcript: z.string().optional(),
})

const textContentSchema = z.object({
  html: z.string(),
  wordCount: z.number(),
  attachments: z.array(z.object({
    url: z.string(),
    name: z.string(),
    type: z.string(),
  })).optional(),
  readingTime: z.number(),
})

const assignmentContentSchema = z.object({
  hints: z.array(z.string()).optional(),
  instructions: z.string(),
  expectedOutput: z.string().optional(),
})

const imageContentSchema = z.object({
  alt: z.string(),
  caption: z.string(),
  imageUrl: z.string(),
  resources: z.array(z.string()).optional(),
})

const downloadContentSchema = z.object({
  files: z.array(z.object({
    url: z.string(),
    name: z.string(),
    size: z.string(),
    type: z.string(),
  })),
  instructions: z.string(),
})

// Union type for different content types
const blockContentSchema = z.union([
  videoContentSchema,
  textContentSchema,
  assignmentContentSchema,
  imageContentSchema,
  downloadContentSchema,
  z.record(z.string(), z.any()) // Fallback for unknown content types
])

// Schema for module info (nested in block)
const moduleInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  course: z.object({
    id: z.string(),
    title: z.string(),
    slug: z.string(),
  }),
})

// Schema for creator info (nested in block)
const creatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable(),
})

// Main block schema
export const blockSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  type: z.enum(['VIDEO', 'TEXT', 'ASSIGNMENT', 'IMAGE', 'DOWNLOAD']),
  content: blockContentSchema,
  order: z.number(),
  duration: z.number(), // Duration in minutes
  isPublished: z.boolean(),
  moduleId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  module: moduleInfoSchema,
  creator: creatorSchema,
})

export type Block = z.infer<typeof blockSchema>
export type VideoContent = z.infer<typeof videoContentSchema>
export type TextContent = z.infer<typeof textContentSchema>
export type AssignmentContent = z.infer<typeof assignmentContentSchema>
export type ImageContent = z.infer<typeof imageContentSchema>
export type DownloadContent = z.infer<typeof downloadContentSchema>

// Schema for the blocks API response (after apiClient extracts data.data)
export const blocksApiResponseSchema = z.object({
  blocks: z.array(blockSchema),
  total: z.number(),
  page: z.number(),
  totalPages: z.number(),
})

export type BlocksApiResponse = z.infer<typeof blocksApiResponseSchema>

// Schema for the complete API response (before apiClient processing)
export const fullBlocksApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    blocks: z.array(blockSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
  }),
  messages: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
})

// Schema for array of blocks (for backward compatibility)
export const blocksArraySchema = z.array(blockSchema)
